import {useMemo, useState, type FC, type RefObject} from 'react'
import {Button, FormControl, Checkbox, Label, CheckboxGroup} from '@primer/react'
import {Blankslate} from '../Blankslate'
import type {
  Rule,
  ErrorRef,
  RuleModalState,
  RuleSchema,
  RuleWithSchema,
  SourceType,
  ValidationError,
} from '@github-ui/repos-rules-types/rules-types'
import {RulesList} from './RulesList'
import {RulesetError} from '../rule-schema/errors/RulesetError'
import styles from './RestrictionsPanel.module.css'
import {PanelHeader} from './conditions/PanelHeader'
import {useUpsellInfoContext} from '../../contexts/UpsellInfoContext'

type MetadataPatternRulesPanelProps = {
  readOnly: boolean
  helpUrl?: string
  title: string
  rules: RuleWithSchema[]
  availableRuleSchemas: RuleSchema[]
  removeRule: (rule: Rule) => void
  restoreRule: (rule: Rule) => void
  setRuleModalState: (rule: Rule, modalState: RuleModalState) => void
  addRule: (type: string) => void
  metadataPatternRuleSchemas: RuleSchema[]
  setMetadataRestrictionTypes: (metadataRestrictionTypes: string[]) => void
  sourceType: SourceType
  ruleErrors: Record<string, ValidationError[]>
  errorRefs: ErrorRef
  editButtonRef?: React.RefObject<HTMLButtonElement | null>
  restrictions: Array<{
    metadataRestrictionTypes: string[]
    label: string
    caption: string
    addRestrictionButtonRef: React.RefObject<HTMLButtonElement | null>
  }>
}

const ProtectionsHeader = () => {
  const upsellInfo = useUpsellInfoContext()
  return (
    <div className="tmp-pt-4" id="restrictionsPanelProtectionsHeader">
      <PanelHeader
        as="h3"
        title="Restrictions"
        trailing={
          upsellInfo.enterpriseRulesets.cta.visible && (
            <div className="d-flex flex-items-center">
              <Label variant="attention" size="large" className="ml-1">
                Enterprise
              </Label>
            </div>
          )
        }
      />
    </div>
  )
}

export const RestrictionsPanel: FC<MetadataPatternRulesPanelProps> = ({
  readOnly,
  helpUrl,
  title,
  rules,
  availableRuleSchemas,
  removeRule,
  restoreRule,
  setRuleModalState,
  addRule,
  metadataPatternRuleSchemas,
  setMetadataRestrictionTypes,
  sourceType,
  ruleErrors,
  errorRefs,
  restrictions,
  editButtonRef,
}) => {
  return rules.length > 0 || !readOnly ? (
    <div>
      <ProtectionsHeader />
      <CheckboxGroup aria-labelledby="restrictionsPanelProtectionsHeader">
        {restrictions.map(restriction => (
          <RestrictionsBox
            key={restriction.label}
            readOnly={readOnly}
            helpUrl={helpUrl}
            title={title}
            rules={rules.filter(rule => restriction.metadataRestrictionTypes.includes(rule.ruleType))}
            removeRule={removeRule}
            restoreRule={restoreRule}
            setRuleModalState={setRuleModalState}
            metadataRestrictionTypes={restriction.metadataRestrictionTypes}
            label={restriction.label}
            caption={restriction.caption}
            availableRuleSchemas={availableRuleSchemas}
            addRule={addRule}
            metadataPatternRuleSchemas={metadataPatternRuleSchemas}
            setMetadataRestrictionTypes={setMetadataRestrictionTypes}
            sourceType={sourceType}
            errors={Object.fromEntries(
              Object.entries(ruleErrors).filter(([errorType, _]) =>
                restriction.metadataRestrictionTypes.includes(errorType),
              ),
            )}
            errorRef={
              // Find the errors associated with metadataRestrictionTypes
              // Then find the first error and get it's errorRef
              errorRefs[
                Object.keys(ruleErrors).find(errorType => restriction.metadataRestrictionTypes.includes(errorType)) ??
                  ''
              ]?.errorRef
            }
            addRestrictionButtonRef={restriction.addRestrictionButtonRef}
            editButtonRef={editButtonRef}
          />
        ))}
      </CheckboxGroup>
    </div>
  ) : (
    <div className={styles.RestrictionsPanelContainer}>
      <div className="mb-1 tmp-mt-3">
        <PanelHeader as="h3" title="Restrictions" />
      </div>
      <div className="Box mt-0">
        <Blankslate heading={'No restrictions have been added'} />
      </div>
    </div>
  )
}

type RestrictionsBoxProps = {
  readOnly: boolean
  helpUrl?: string
  title: string
  rules: RuleWithSchema[]
  removeRule: (rule: Rule) => void
  restoreRule: (rule: Rule) => void
  setRuleModalState: (rule: Rule, modalState: RuleModalState) => void
  metadataRestrictionTypes: string[]
  label: string
  caption: string
  availableRuleSchemas: RuleSchema[]
  addRule: (type: string) => void
  metadataPatternRuleSchemas: RuleSchema[]
  setMetadataRestrictionTypes: (metadataRestrictionTypes: string[]) => void
  sourceType: SourceType
  errors: Record<string, ValidationError[]>
  errorRef?: RefObject<HTMLDivElement | null>
  addRestrictionButtonRef?: React.RefObject<HTMLButtonElement | null>
  editButtonRef?: React.RefObject<HTMLButtonElement | null>
}

export const RestrictionsBox: FC<RestrictionsBoxProps> = ({
  readOnly,
  helpUrl,
  title,
  rules,
  removeRule,
  restoreRule,
  setRuleModalState,
  metadataRestrictionTypes,
  label,
  caption,
  availableRuleSchemas,
  addRule,
  metadataPatternRuleSchemas,
  setMetadataRestrictionTypes,
  sourceType,
  errors,
  errorRef,
  addRestrictionButtonRef,
  editButtonRef,
}) => {
  const [boxChecked, setBoxChecked] = useState(rules.length > 0)

  const hasErrors = useMemo(() => {
    return Object.values(errors).flat().length > 0
  }, [errors])

  return (
    <div className={`Box-row d-flex flex-justify-between flex-items-center gap-2 pl-0`}>
      <div className={`d-flex flex-1 flex-column`}>
        {!readOnly ? (
          <>
            <FormControl>
              <Checkbox
                checked={boxChecked}
                onChange={() => {
                  if (boxChecked) {
                    for (const rule of rules) {
                      removeRule(rule)
                    }
                  }
                  setBoxChecked(!boxChecked)
                }}
              />
              <FormControl.Label>{label}</FormControl.Label>
              <FormControl.Caption>{caption}</FormControl.Caption>
            </FormControl>
            {boxChecked && hasErrors && (
              <div className="pl-2">
                <div className="tmp-px-3">
                  <RulesetError
                    errorRef={errorRef}
                    errorId={`${label}-error`}
                    sourceType={sourceType}
                    fields={[]}
                    errors={Object.values(errors).flat()}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            <span className="text-bold">{label}</span>
            <span className="d-block text-small color-fg-muted">{caption}</span>
          </div>
        )}
        {boxChecked && (
          <div className="pl-2 mt-2">
            <div className="tmp-px-3 py-2 d-flex flex-column">
              <div className="d-flex flex-column gap-2">
                <div className={`Box`}>
                  <div className="Box-header d-flex flex-items-center p-2">
                    <div className="Box-title flex-1">Metadata restrictions</div>
                    {!readOnly && (
                      <Button
                        ref={addRestrictionButtonRef}
                        className="ml-2"
                        type="button"
                        size="small"
                        onClick={() => {
                          setMetadataRestrictionTypes(metadataRestrictionTypes)
                          addRule(
                            availableRuleSchemas.find(option => metadataRestrictionTypes.includes(option.type))?.type ||
                              metadataPatternRuleSchemas.find(option => metadataRestrictionTypes.includes(option.type))!
                                .type,
                          )
                        }}
                      >
                        Add restriction
                      </Button>
                    )}
                  </div>
                  <RulesList
                    readOnly={readOnly}
                    helpUrl={helpUrl}
                    title={title}
                    rules={rules}
                    removeRule={removeRule}
                    restoreRule={restoreRule}
                    setRuleModalState={setRuleModalState}
                    errors={Object.values(errors).flat() || []}
                    editButtonRef={editButtonRef}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
